"use client";

import Image from "next/image";
import Link from "next/link";
import React, { useRef, useState, useEffect } from "react";

interface GalleryItem {
  image?: {
    secure_url?: string;
    context?: {
      custom?: {
        alt?: string;
        caption?: string;
        title?: string;
      };
    };
    description?: string;
  };
  caption?: string;
}

interface ScrollableGalleryProps {
  images: GalleryItem[];
  heading?: string;
  description?: string;
}

export default function ScrollableGallery({
  images,
  heading,
  description,
}: ScrollableGalleryProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showButtons, setShowButtons] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Filter out invalid images
  const validImages = images.filter(
    (image) => image.image?.secure_url && image.image
  );

  useEffect(() => {
    // Show buttons only if there are more than 3 images
    setShowButtons(validImages.length > 3);

    const checkScrollability = () => {
      const container = scrollContainerRef.current;
      if (!container) return;

      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 1
      );
    };

    const container = scrollContainerRef.current;
    if (!container) return;

    // Check on mount and resize
    checkScrollability();
    window.addEventListener("resize", checkScrollability);
    container.addEventListener("scroll", checkScrollability);

    return () => {
      window.removeEventListener("resize", checkScrollability);
      container.removeEventListener("scroll", checkScrollability);
    };
  }, [validImages.length]);

  const scroll = (direction: "left" | "right") => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = container.clientWidth * 0.8; // Scroll by 80% of container width
    const targetScroll =
      direction === "left"
        ? container.scrollLeft - scrollAmount
        : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: targetScroll,
      behavior: "smooth",
    });
  };

  if (validImages.length === 0) return null;

  return (
    <section className="py-12">
      <div className="mx-auto max-w-6xl">
        <h2 className="text-center text-3xl font-bold text-violet-950">
          {heading || "Before & After"}
        </h2>
        {description && (
          <p className="mt-2 text-center text-gray-500">{description}</p>
        )}
        <div className="relative mt-10">
          {/* Left Scroll Button */}
          {showButtons && (
            <button
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              className={`absolute left-0 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white p-3 shadow-lg transition-all hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed ${
                canScrollLeft ? "opacity-100" : "opacity-50"
              }`}
              aria-label="Scroll left"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-6 w-6 text-violet-950"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 19.5L8.25 12l7.5-7.5"
                />
              </svg>
            </button>
          )}

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className={`gap-6 ${
              showButtons
                ? "flex overflow-x-auto scrollbar-hide px-12"
                : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
            }`}
            style={
              showButtons
                ? {
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                  }
                : {}
            }
          >
            {validImages.map((image, index) => {
              const url = image.image?.secure_url;
              if (!url || !image.image) return null;
              const alt =
                image.image.context?.custom?.alt ||
                image.image.context?.custom?.title ||
                image.image.description ||
                "Hair transplant result";
              return (
                <figure
                  key={`${url}-${index}`}
                  className={`overflow-hidden rounded-2xl border border-violet-100 bg-white shadow-sm ${
                    showButtons
                      ? "min-w-[280px] flex-shrink-0 md:min-w-[320px]"
                      : ""
                  }`}
                >
                  <Image
                    src={url}
                    alt={alt}
                    width={640}
                    height={420}
                    className="h-64 w-full object-cover"
                    loading="lazy"
                  />
                  {alt && alt !== "Hair transplant result" && (
                    <figcaption className="p-4 text-sm text-gray-600">
                      {alt}
                    </figcaption>
                  )}
                </figure>
              );
            })}
          </div>

          {/* Right Scroll Button */}
          {showButtons && (
            <button
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              className={`absolute right-0 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white p-3 shadow-lg transition-all hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed ${
                canScrollRight ? "opacity-100" : "opacity-50"
              }`}
              aria-label="Scroll right"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-6 w-6 text-violet-950"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8.25 4.5l7.5 7.5-7.5 7.5"
                />
              </svg>
            </button>
          )}
        </div>
        <p className="mt-6 text-center text-sm italic text-gray-500">
          *Disclaimer : Results may vary from person to person*
        </p>
        <div className="mt-6 text-center">
          <Link
            href="https://www.trichos.in/gallery"
            className="inline-flex items-center gap-2 rounded-2xl border border-[#F7E9AE] bg-transparent px-6 py-3 text-sm font-semibold text-[#F7E9AE] transition-colors hover:opacity-90"
          >
            View Gallery
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="h-4 w-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
              />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}

